import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useMeta } from "../context/MetaContext";

const aboutMenu = [
  {
    title: "What is RelayGraph",
    text: "An internal AI consulting operations platform that turns execution frameworks into focused tools.",
    href: "/"
  },
  {
    title: "Vision & Mission",
    text: "Build niche high-impact systems that remove delivery friction and improve consulting outcomes.",
    href: "/"
  },
  {
    title: "Career",
    text: "Join product, consulting, and applied AI teams building practical enterprise-grade tools.",
    href: "/"
  },
  {
    title: "Goals & Achievements",
    text: "A roadmap focused on measurable customer impact, delivery quality, and strategic speed.",
    href: "/"
  }
];

export default function PortalHeader({
  user,
  authenticated = false,
  onLogout,
  inWorkspace = false,
  onExitWorkspace
}) {
  const { toolIndustryGroups } = useMeta();
  const [openMenu, setOpenMenu] = useState("");
  const closeTimeoutRef = useRef(null);

  const workspaceHref = authenticated ? "/tools/wpa/dashboard" : "/login?next=%2Ftools%2Fwpa%2Fdashboard";
  const firstName = useMemo(() => String(user?.name || "User").trim().split(" ")[0], [user]);

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const openMega = (key) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setOpenMenu(key);
  };

  const closeMega = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenu("");
    }, 160);
  };

  return (
    <header className="portal-header">
      <div className="portal-header-inner">
        <div className="portal-brand">
          <Link to="/" className="brand-logo-mark" aria-label="RelayGraph home">
            <img src="/brand/relaygraph-logo.png" alt="RelayGraph" className="brand-logo-image" />
          </Link>
        </div>

        <nav className="portal-global-nav">
          <NavLink to="/" className={({ isActive }) => `portal-link ${isActive ? "active" : ""}`}>Home</NavLink>

          <div className="menu-wrap" onMouseEnter={() => openMega("about")} onMouseLeave={closeMega}>
            <button type="button" className="portal-link menu-trigger">About</button>
            {openMenu === "about" ? (
              <div className="mega-menu mega-menu-about">
                <div className="mega-hero">
                  <p className="mega-eyebrow">About RelayGraph</p>
                  <h3>Consulting Workflows Reimagined As Productized Tools</h3>
                  <p>Practical interfaces built for execution quality, strategic speed, and leadership clarity.</p>
                </div>
                <div className="mega-grid">
                  {aboutMenu.map((item) => (
                    <Link key={item.title} to={item.href} className="mega-item-card">
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="menu-wrap" onMouseEnter={() => openMega("tools")} onMouseLeave={closeMega}>
            <button type="button" className="portal-link menu-trigger">Tools</button>
            {openMenu === "tools" ? (
              <div className="mega-menu mega-menu-tools">
                <div className="mega-hero">
                  <p className="mega-eyebrow">50+ Niche Tools</p>
                  <h3>Industry-First Tool Library</h3>
                  <p>Launch execution-focused systems for technology, operations, delivery, and business transformation.</p>
                  <div className="mega-hero-actions">
                    <Link to="/tools/wpa" className="mini-link">WordPress Landing</Link>
                    <Link to={workspaceHref} className="mini-link filled">Launch WordPress Workspace</Link>
                  </div>
                </div>
                {toolIndustryGroups.map((group) => (
                  <div key={group.industry} className="mega-col">
                    <h4>{group.industry}</h4>
                    {group.tools.map((tool) => (
                      <Link
                        key={tool.slug}
                        to={tool.permalink}
                        className="mega-item"
                      >
                        {tool.name}
                      </Link>
                    ))}
                  </div>
                ))}
                {!toolIndustryGroups.length ? (
                  <div className="mega-col">
                    <h4>Tools</h4>
                    <span className="muted">No tools configured yet.</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <Link className="portal-link plain" to="/">Career</Link>
          <Link className="portal-link plain" to="/">Blogs</Link>
          <Link className="portal-link plain" to="/">Contact Us</Link>
        </nav>

        <div className="portal-user">
          {inWorkspace && onExitWorkspace ? (
            <button type="button" className="top-exit-btn" onClick={onExitWorkspace}>
              Exit Workspace
            </button>
          ) : null}

          {!authenticated ? (
            <>
              <button type="button" className="icon-circle-btn" aria-label="Platform language and region">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.9 9h-3.1a15 15 0 0 0-1.3-5A8.03 8.03 0 0 1 18.9 11zM12 4.1c.9 1.2 1.8 3.5 2.1 6.9H9.9c.3-3.4 1.2-5.7 2.1-6.9zM6.5 6a15 15 0 0 0-1.3 5H2.1A8.03 8.03 0 0 1 6.5 6zM4.1 13h3.1c.2 1.9.7 3.7 1.3 5A8.03 8.03 0 0 1 4.1 13zm7.9 6.9c-.9-1.2-1.8-3.5-2.1-6.9h4.2c-.3 3.4-1.2 5.7-2.1 6.9zm2.5-1.9c.6-1.3 1.1-3.1 1.3-5h3.1a8.03 8.03 0 0 1-4.4 5z" />
                </svg>
              </button>
              <Link to="/login" className="auth-cta auth-cta-ghost">Sign In</Link>
              <Link to="/login" className="auth-cta auth-cta-solid">Sign Up</Link>
            </>
          ) : (
            <>
              <button type="button" className="icon-circle-btn" aria-label="Notifications">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3a5 5 0 0 0-5 5v2.3c0 .7-.2 1.4-.7 2l-1 1.3c-.8 1-.1 2.4 1.1 2.4h11.2c1.2 0 1.9-1.4 1.1-2.4l-1-1.3a3.4 3.4 0 0 1-.7-2V8a5 5 0 0 0-5-5zm0 18a2.5 2.5 0 0 0 2.4-2h-4.8A2.5 2.5 0 0 0 12 21z" />
                </svg>
              </button>
              <div className="profile-menu" onMouseEnter={() => openMega("profile")} onMouseLeave={closeMega}>
                <button type="button" className="profile-trigger" aria-label="Open profile menu" onClick={() => setOpenMenu((current) => current === "profile" ? "" : "profile")}>
                  <img src="/brand/avatar-default.svg" alt="Profile avatar" className="profile-avatar-img" />
                </button>
                {openMenu === "profile" ? (
                  <div className="profile-panel">
                    <div className="profile-summary-card">
                      <img src="/brand/avatar-default.svg" alt="Profile avatar" className="profile-avatar-img large" />
                      <div>
                        <strong>{firstName}</strong>
                        <span>Consultant</span>
                      </div>
                    </div>
                    <Link to="/dashboard"><span className="icon-tag">P</span> My Profile</Link>
                    <Link to="/settings"><span className="icon-tag">S</span> Settings</Link>
                    <button type="button" onClick={onLogout}><span className="icon-tag">O</span> Sign Out</button>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
